import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  fetchAdminTeamSubscriptions,
  fetchSystemUserInfo,
  readTeamCountry,
  type TeamSubscriptionOrder,
  type TeamSubscriptionOrderStatus,
  type TeamSubscriptionType,
} from '@/api';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import { parseBackendDateTime } from '@/utils/parseBackendDateTime';
import { DEFAULT_TEAM_COUNTRY, normalizeTeamCountry, teamCountryCurrency } from '@/constants/teamCountries';

const PAGE_SIZE = 10;

type StatusTab = 'all' | TeamSubscriptionOrderStatus;
type TypeTab = 'all' | TeamSubscriptionType;

const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 1, label: 'Paid' },
  { key: 0, label: 'Unpaid' },
];

const TYPE_TABS: { key: TypeTab; label: string }[] = [
  { key: 'all', label: 'All types' },
  { key: 'subscription', label: 'Plan' },
  { key: 'extra_members', label: 'Extra seats' },
  { key: 'trial', label: 'Trial' },
];

const TYPE_LABELS: Record<string, string> = {
  subscription: 'Subscription',
  extra_members: 'Extra seats',
  trial: 'Trial',
};

function parseStatusTab(value: string | null): StatusTab {
  if (value === '0' || value === '1') return Number(value) as TeamSubscriptionOrderStatus;
  return 'all';
}

function parseTypeTab(value: string | null): TypeTab {
  if (value === 'subscription' || value === 'extra_members' || value === 'trial') return value;
  return 'all';
}

function formatMoney(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function formatDateTime(raw?: string): string {
  if (!raw) return '—';
  const parsed = parseBackendDateTime(raw);
  if (!parsed) return raw;
  return dayjs(parsed).format('D MMM YYYY, h:mm A');
}

function statusLabel(status: TeamSubscriptionOrderStatus): string {
  return status === 1 ? 'Paid' : 'Unpaid';
}

function statusBadgeClass(status: TeamSubscriptionOrderStatus): string {
  return status === 1
    ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
}

function typeBadgeClass(type: string): string {
  if (type === 'trial') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (type === 'extra_members') return 'bg-teal-50 text-teal-700 border-teal-200';
  return 'bg-green-50 text-green-700 border-green-200';
}

function paymentMethodLabel(method?: string): string | null {
  if (!method) return null;
  const key = method.trim().toLowerCase();
  if (key === 'stripe') return 'Stripe';
  if (key === 'paypal') return 'PayPal';
  if (key === 'free_trial') return 'Free trial';
  if (key === 'bank_transfer') return 'Bank transfer';
  return method.replace(/_/g, ' ');
}

function seatLabel(order: TeamSubscriptionOrder): string {
  if (order.subscriptionType === 'extra_members') {
    const n = order.extraMembers;
    return `+${n} extra seat${n === 1 ? '' : 's'}`;
  }
  const base = order.baseMembers;
  const extra = order.extraMembers;
  if (typeof base === 'number' && base > 0) {
    const total = base + extra;
    return extra > 0
      ? `${total} seats (${base} included + ${extra} extra)`
      : `${total} seat${total === 1 ? '' : 's'}`;
  }
  if (extra > 0) return `${extra} extra seat${extra === 1 ? '' : 's'}`;
  return '—';
}

function monthsLabel(months: number): string {
  if (months <= 0) return '—';
  return months === 1 ? '1 month' : `${months} months`;
}

export default function SubscribeOrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeStatus, setActiveStatus] = useState<StatusTab>(parseStatusTab(searchParams.get('status')));
  const [activeType, setActiveType] = useState<TypeTab>(parseTypeTab(searchParams.get('type')));
  const [records, setRecords] = useState<TeamSubscriptionOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [priceCountry, setPriceCountry] = useState(DEFAULT_TEAM_COUNTRY);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef(1);
  const totalPagesRef = useRef(0);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    totalPagesRef.current = totalPages;
  }, [totalPages]);

  const loadFirstPage = useCallback(async (status: StatusTab, type: TypeTab) => {
    setLoading(true);
    setListError(null);
    setPage(1);
    pageRef.current = 1;

    try {
      const result = await fetchAdminTeamSubscriptions({
        page: 1,
        pageSize: PAGE_SIZE,
        status: status === 'all' ? undefined : status,
        subscriptionType: type === 'all' ? undefined : type,
      });
      setRecords(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
      totalPagesRef.current = result.totalPages;
    } catch (err) {
      setRecords([]);
      setTotal(0);
      setTotalPages(0);
      totalPagesRef.current = 0;
      setListError(err instanceof Error ? err.message : 'Failed to load subscription orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFirstPage(activeStatus, activeType);
  }, [activeStatus, activeType, loadFirstPage]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile = await fetchSystemUserInfo();
        if (cancelled) return;
        setPriceCountry(normalizeTeamCountry(readTeamCountry(profile.team_info)));
      } catch {
        if (!cancelled) setPriceCountry(DEFAULT_TEAM_COUNTRY);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || loading || loadingMore) return;
    if (totalPagesRef.current === 0 || pageRef.current >= totalPagesRef.current) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = pageRef.current + 1;

    try {
      const result = await fetchAdminTeamSubscriptions({
        page: nextPage,
        pageSize: PAGE_SIZE,
        status: activeStatus === 'all' ? undefined : activeStatus,
        subscriptionType: activeType === 'all' ? undefined : activeType,
      });
      if (result.items.length === 0) {
        setTotalPages(pageRef.current);
        totalPagesRef.current = pageRef.current;
        return;
      }
      setTotal(result.total);
      setTotalPages(result.totalPages);
      totalPagesRef.current = result.totalPages;
      setPage(nextPage);
      pageRef.current = nextPage;
      setRecords((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        const next = [...prev];
        for (const row of result.items) {
          if (!seen.has(row.id)) {
            seen.add(row.id);
            next.push(row);
          }
        }
        return next;
      });
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load more subscription orders.');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [activeStatus, activeType, loading, loadingMore]);

  const hasMore = totalPages > 0 && page < totalPages;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        void loadMore();
      },
      { root: null, rootMargin: '120px', threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const updateQuery = (status: StatusTab, type: TypeTab) => {
    const next = new URLSearchParams(searchParams);
    if (status === 'all') next.delete('status');
    else next.set('status', String(status));
    if (type === 'all') next.delete('type');
    else next.set('type', type);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] pb-6">
      <SubpageHeader
        title="Subscription orders"
        subtitle="Plan, extra seats, and trial payments"
        onBack={() => navigate('/subscribe')}
      >
        <div className="px-4">
          <div className="flex border-b border-gray-200 -mx-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={String(tab.key)}
                type="button"
                onClick={() => {
                  setActiveStatus(tab.key);
                  updateQuery(tab.key, activeType);
                }}
                className={`flex-1 py-2.5 text-xs font-semibold cursor-pointer whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  activeStatus === tab.key
                    ? 'text-green-600 border-green-600'
                    : 'text-gray-400 border-transparent'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1.5 py-2.5 overflow-x-auto">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setActiveType(tab.key);
                  updateQuery(activeStatus, tab.key);
                }}
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold border cursor-pointer ${
                  activeType === tab.key
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-white text-gray-500 border-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </SubpageHeader>

      <div className="px-4 pt-4 space-y-3">
        {listError && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {listError}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
            <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
            <p className="text-gray-400 text-xs mt-2">Loading subscription orders…</p>
          </div>
        ) : records.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
            <i className="ri-file-list-3-line text-gray-300 text-3xl" />
            <p className="text-gray-400 text-xs mt-2">No subscription orders found</p>
          </div>
        ) : (
          records.map((record) => {
            const method = paymentMethodLabel(record.paymentMethod);
            return (
              <div key={record.id} className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${typeBadgeClass(record.subscriptionType)}`}
                      >
                        {TYPE_LABELS[record.subscriptionType] ?? 'Subscription'}
                      </span>
                      <span className="text-gray-400 text-[10px]">#{record.id}</span>
                    </div>
                    <div className="text-gray-900 font-bold text-lg leading-none mt-2">
                      {teamCountryCurrency(priceCountry).symbol}{formatMoney(record.totalPrice)}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${statusBadgeClass(record.status)}`}
                  >
                    {statusLabel(record.status)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                    <div className="text-gray-400 text-[10px] font-medium">Duration</div>
                    <div className="text-gray-800 text-sm font-bold">{monthsLabel(record.months)}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                    <div className="text-gray-400 text-[10px] font-medium">Seats</div>
                    <div className="text-gray-800 text-sm font-bold">{seatLabel(record)}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-gray-500 text-[11px]">
                  <i className="ri-calendar-line text-green-500 text-xs" />
                  <span>
                    {formatDateTime(record.startTime)} – {formatDateTime(record.expireTime)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-[10px] mt-1">
                  <i className="ri-time-line" />
                  <span>Created {formatDateTime(record.createTime)}</span>
                </div>
                {record.status === 1 && record.paymentTime && (
                  <div className="flex items-center gap-2 text-gray-400 text-[10px] mt-0.5">
                    <i className="ri-checkbox-circle-line" />
                    <span>
                      Paid {formatDateTime(record.paymentTime)}
                      {method ? ` · ${method}` : ''}
                    </span>
                  </div>
                )}
              </div>
            );
          })
        )}

        {!loading && hasMore && (
          <div ref={sentinelRef} className="py-2.5 text-center">
            {loadingMore ? (
              <i className="ri-loader-4-line animate-spin text-green-500 text-xl" />
            ) : (
              <span className="text-gray-300 text-xs">Scroll for more</span>
            )}
          </div>
        )}

        {!loading && !hasMore && records.length > 0 && (
          <p className="text-center text-gray-300 text-[10px] pb-2">
            {total} order{total === 1 ? '' : 's'} total
          </p>
        )}
      </div>
    </div>
  );
}
